import React, { useRef, useState, useEffect } from "react";
import "./QuickTimeMovieEmbed.scss";

// Use public assets as strings
const playButton = "/assets/icons/quicktime/play-button.svg";
const pauseButton = "/assets/icons/quicktime/pause-button.svg";

type QuickTimeVideoEmbedProps = {
  appId: string;
  name: string;
  url: string;
  type: "audio" | "video";
  visualUrl?: string;
  options?: {};
  subtitlesUrl?: string;
  autoPlay?: boolean;
  hideControls?: boolean;
  controlsDocked?: boolean;
  muted?: boolean;
};

export const QuickTimeVideoEmbed: React.FC<QuickTimeVideoEmbedProps> = ({
  appId,
  name,
  url,
  type,
  visualUrl,
  autoPlay = false,
  hideControls = false,
  controlsDocked = true,
  muted = false,
}) => {
  const [playing, setPlaying] = useState(autoPlay);
  const [volume, setVolume] = useState(0.5);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [showVolume, setShowVolume] = useState(false);

  const mediaRef = useRef<HTMLAudioElement | HTMLVideoElement>(null);
  const volumeButtonRef = useRef<HTMLButtonElement>(null);

  // Handle play/pause state changes
  useEffect(() => {
    if (mediaRef.current) {
      if (playing) {
        mediaRef.current.play().catch((e) => console.error("Play error:", e));
      } else {
        mediaRef.current.pause();
      }
    }
  }, [playing]);

  // Handle volume changes
  useEffect(() => {
    if (mediaRef.current) {
      mediaRef.current.volume = muted ? 0 : volume;
    }
  }, [volume, muted]);

  const handlePlayPause = () => {
    setPlaying(!playing);
  };

  const handleTimeUpdate = () => {
    if (mediaRef.current) {
      setCurrentTime(mediaRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (mediaRef.current) {
      setDuration(mediaRef.current.duration);
    }
  };

  const seekToPct = (pct: number) => {
    if (mediaRef.current && duration > 0) {
      mediaRef.current.currentTime = pct * duration;
    }
  };

  const timeFriendly = (seconds: number): string => {
    if (!Number.isFinite(seconds)) {
      return "0:00:00";
    }
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    return `${hours}:${String(minutes).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
  };

  const getVolumeIcon = (vol: number): string => {
    if (vol === 0) return "sound-off.png";
    if (vol > 0 && vol < 0.3) return "sound-33.png";
    if (vol >= 0.3 && vol < 0.7) return "sound-66.png";
    return "sound-on.png";
  };

  return (
    <div
      className="quickTimePlayerWrapper"
      style={{
        height: controlsDocked
          ? "calc(100% - var(--window-control-size) * 1)"
          : "100%",
      }}
    >
      <div
        className="quickTimePlayerVideoHolder"
        style={{
          height: controlsDocked
            ? "calc(100% - var(--window-control-size) * 1)"
            : "calc(100% - var(--window-border-size) * 4)",
        }}
      >
        {type === "video" ? (
          <video
            ref={mediaRef as React.RefObject<HTMLVideoElement>}
            src={url}
            onTimeUpdate={handleTimeUpdate}
            onLoadedMetadata={handleLoadedMetadata}
            style={{
              width: "100%",
              height: "100%",
              backgroundColor: "#CCCCCC",
            }}
          />
        ) : (
          <>
            <audio
              ref={mediaRef as React.RefObject<HTMLAudioElement>}
              src={url}
              onTimeUpdate={handleTimeUpdate}
              onLoadedMetadata={handleLoadedMetadata}
            />
            {visualUrl && (
              <div
                style={{
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "#CCCCCC",
                }}
              >
                <img
                  src={visualUrl}
                  alt={name}
                  style={{
                    maxWidth: "100%",
                    maxHeight: "100%",
                    objectFit: "contain",
                  }}
                />
              </div>
            )}
          </>
        )}
      </div>
      {!hideControls && (
        <div
          className="quickTimePlayerVideoControlsHolder"
          style={{ position: controlsDocked ? "absolute" : "relative" }}
        >
          <button
            onClick={handlePlayPause}
            className="quickTimePlayerVideoControlsButton"
          >
            <img
              className="quickTimePlayerVideoControlsIcon"
              src={playing ? pauseButton : playButton}
              alt={playing ? "Pause" : "Play"}
            />
          </button>
          <div className="quickTimePlayerVideoControlsProgressBarHolder">
            <input
              id={appId + "_" + name + "_progressBar"}
              className="quickTimePlayerVideoControlsProgressBar"
              type="range"
              min="0"
              max="1"
              step="0.001"
              value={duration > 0 ? currentTime / duration : 0}
              onChange={(e) => {
                seekToPct(parseFloat(e.target.value));
              }}
            />
          </div>
          <p className="quickTimePlayerVideoControlsTime">
            {timeFriendly(currentTime)}
          </p>

          {showVolume && (
            <div
              style={{
                zIndex: 999999,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <input
                className="quickTimePlayerVideoControlsVolumeBar"
                id={url + "_volume"}
                type="range"
                min="0"
                max="1"
                step="0.1"
                style={{
                  left: volumeButtonRef.current?.offsetLeft,
                }}
                value={1 - volume}
                onClick={() => {
                  setShowVolume(false);
                }}
                onChange={(e) => {
                  setVolume(1 - parseFloat(e.target.value));
                }}
              />
            </div>
          )}
          <button
            className="quickTimePlayerVideoControlsButton"
            onClick={() => setShowVolume(!showVolume)}
            ref={volumeButtonRef}
          >
            <img
              src={`/assets/icons/quicktime/${getVolumeIcon(volume)}`}
              className="quickTimePlayerVideoControlsIcon"
              alt="Volume"
              onError={(e) => {
                // Fallback if volume icons don't exist
                e.currentTarget.style.display = "none";
              }}
            />
          </button>
        </div>
      )}
    </div>
  );
};
